import { useCallback, useMemo, useState } from "react";
import { ToolContainer, FileDropZone } from "@/components/ToolLayout";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Check, X, AlertTriangle } from "lucide-react";
import { analyzeDocx, BAD_DOCX_MESSAGE, type DocParts, type FormatReport, type Style } from "./paperFormat";
import { readDocxParts } from "./paperRead";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { ToolInfoSection } from "@/components/ToolInfoSection";

function CheckRow({
  label,
  pass,
  detail,
}: {
  label: string;
  pass: boolean | null;
  detail: string;
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      {pass === null ? (
        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
      ) : pass ? (
        <Check className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
      ) : (
        <X className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
      )}
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </div>
    </div>
  );
}

const TOOL_INFO_CONTENT = {
  what: `Reads a real .docx file's margins, fonts, sizes, line spacing, paragraph spacing, indents and page numbers as Word applies them, and checks them against APA, MLA, or Chicago formatting requirements — an actual read of the document's formatting, not a guess based on word count.`,
  how: `Choose which style to check against, upload your .docx paper, and each formatting requirement is checked against what's actually set in the document, with a clear pass, fail, or undetected result for each.`,
  tip: `Each result is weighted by how much of your body text it covers, so a mixed-font paper is reported with the percentages — fix the largest share first, and still give it a final visual check.`,
};

export default function PaperFormatChecker() {
  const [style, setStyle] = useState<Style>("APA");
  const [file, setFile] = useState<File | null>(null);
  const [parts, setParts] = useState<DocParts | null>(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState("");
  const { logUsageNow } = useLogUsageOnAction();

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    setProblem("");
    if (!f.name.toLowerCase().endsWith(".docx")) {
      // Said on the page (not only in a toast that disappears); the drop zone stays so another file can be dropped.
      setProblem(`"${f.name}" isn't a .docx file. This tool reads Word .docx files: save your paper as .docx first if it's in another format.`);
      return;
    }
    setFile(f);
    setLoading(true);
    try {
      const read = await readDocxParts(f);
      // The trial use is taken when the report is shown, not when a file is chosen (an unreadable file costs nothing).
      if (!(await logUsageNow())) { setFile(null); return; }
      setParts(read);
    } catch {
      // A corrupt or renamed file: say so and bring the drop zone back instead of leaving a blank page.
      setFile(null);
      setParts(null);
      setProblem(BAD_DOCX_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, [logUsageNow]);

  const report: FormatReport | null = useMemo(() => {
    if (!parts) return null;
    try { return analyzeDocx(parts, style); } catch { return null; }
  }, [parts, style]);

  return (
    <ToolContainer>
      <Label htmlFor="paper-style" className="mb-2 block text-sm">Check against</Label>
      <Select value={style} onValueChange={v => setStyle(v as Style)}>
        <SelectTrigger id="paper-style" className="max-w-xs mb-6">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="APA">APA (7th edition)</SelectItem>
          <SelectItem value="MLA">MLA (9th edition)</SelectItem>
          <SelectItem value="Chicago">Chicago (17th edition)</SelectItem>
        </SelectContent>
      </Select>

      {problem && <p role="alert" data-testid="paper-problem" className="mb-3 text-sm text-destructive">{problem}</p>}
      {!file ? (
        <FileDropZone
          gateAtDownload
          accept=".docx"
          onFiles={handleFiles}
          label="Drop your .docx paper"
          description="Checks your document's actual margins, font, and line spacing against the style guide — reads real formatting, not just word count"
        />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">
          Reading document formatting...
        </p>
      ) : report ? (
        <div className="space-y-4">
          <div className="p-3 rounded-lg border bg-card flex items-center justify-between gap-3">
            <p className="text-sm font-medium">{file.name}</p>
            <button className="text-xs underline text-muted-foreground" onClick={() => { setFile(null); setParts(null); }}>Check another file</button>
          </div>

          <Card className="p-4">
            {report.checks.map(c => (
              <CheckRow key={c.id} label={c.label} pass={c.pass} detail={c.detail} />
            ))}
          </Card>

          <p className="text-xs text-muted-foreground">
            Reads the formatting Word actually applies — the style chain, document defaults, theme fonts, direct
            paragraph and run formatting, every section's margins and the page-number fields — weighted by how much
            body text each setting covers, so a single heading in another font doesn't fail the paper. Text in tables
            and text boxes, footnotes, citations, title-page structure and running heads aren't checked, and your
            instructor's requirements always come first.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p role="alert" data-testid="paper-problem" className="text-sm text-destructive">{BAD_DOCX_MESSAGE}</p>
          <button className="text-xs underline text-muted-foreground" onClick={() => { setFile(null); setParts(null); }}>Check another file</button>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
